import type {
  MegaJSON,
  StudioEnvironments,
  ReferencedAppSettings,
  BaseAppSettings
} from "@certa/studio-core";
import type { AppSettings } from "@certa/queries/types/appSettings.types";
import { analyzeAppSettings } from "@certa/studio-core/src/utils/megaJSON.utils";
import { isEqual } from "lodash-es";
import {
  getBaseAppSettingsFromMegaJSON,
  getReferencedAppSettingsModelCreator
} from "../hooks/dbOpsSlices/appsettings.slice";
import {
  mergeAppSettings,
  filterAppSettingsByOriginal,
  getUniqueSettings
} from "./reconcileReferencedAppSettings.utils";

export const reconcileReferencedAppSettings = ({
  megaJSON,
  appSettings,
  environment,
  skipMergingOldReferenceSettings = false // Skips merging old reference settings and uses just based on current config
}: {
  megaJSON: MegaJSON;
  appSettings: AppSettings;
  environment: StudioEnvironments;
  skipMergingOldReferenceSettings?: boolean;
}): {
  referencedAppSettings: ReferencedAppSettings;
  shouldUpdateReferenceSettings: boolean;
  newBaseAppSettings: BaseAppSettings;
} => {
  // Compute referenced settings across all processes once
  const allProcesses = Object.values(megaJSON.processes);
  const { referencedAppSettings: newReferencedAppSettings } =
    analyzeAppSettings(allProcesses, appSettings);

  // Get existing settings
  const existingAppSettings = getReferencedAppSettingsModelCreator(
    megaJSON.app_settings
  );

  // Merge existing settings with new ones, giving preference to new settings
  // Skip merging if flag is set
  const referencedAppSettings = skipMergingOldReferenceSettings
    ? newReferencedAppSettings
    : mergeAppSettings(existingAppSettings, newReferencedAppSettings);

  // Extract ReferencedAppSettings portion from baseAppSettings
  const baseAppSettings = getBaseAppSettingsFromMegaJSON(megaJSON);
  // Use existing merge functions - base settings first to take precedence, new items added only if not present
  const mergedReferencedSettingsForBase = mergeAppSettings(
    referencedAppSettings,
    baseAppSettings
  );

  const newBaseAppSettings: BaseAppSettings = filterAppSettingsByOriginal(
    mergedReferencedSettingsForBase,
    appSettings
  );

  const oldAppSettings = megaJSON.app_settings;
  const shouldUpdateReferenceSettings =
    !isEqual(
      getUniqueSettings(referencedAppSettings),
      getUniqueSettings(getReferencedAppSettingsModelCreator(oldAppSettings))
    ) ||
    !isEqual(
      getUniqueSettings(newBaseAppSettings),
      getUniqueSettings(baseAppSettings)
    );

  return {
    referencedAppSettings,
    shouldUpdateReferenceSettings,
    newBaseAppSettings
  };
};
